import { useEffect, useMemo, useState } from "react"
import {
  KInput,
  KSpan,
  KTitleSpan,
  KDropdown,
  KButton,
  KLogo,
  KTooltip,
  KSelectRangeDate
} from "kahuna-base-react-components"
import TableNew from "../../ui/table/TableNew"
import { getSelectedLanguage, lang } from "../../../constants/languages"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { PaginatedData, SpotifyChartEntry, TableColumn } from "../../../types"
import { fetchSpotifyChartEntries } from "../../../actions/insightsActions"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { useNavigate } from "react-router-dom"
//@ts-ignore
import ArtistLogo from "../../../assets/spotify-charts/microphone.svg"
//@ts-ignore
import AlbumLogo from "../../../assets/spotify-charts/record-label.svg"
//@ts-ignore
import TrackLogo from "../../../assets/spotify-charts/music-note-1.svg"
import SearchUserComponent from "../../ui/components/SearchUserComponent"
import spotifyChartEntriesColumns from "./spotifyChartEntriesColumns"
import { formatDate, getDefaultDatesForSpotifyChart, parseDate, toDashedYYYYMMDD, toYYYYMMDD } from "../../../utility"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRangeDate/KSelectRangeDate"
import { spotifyChartEntries } from "../../../apis"

enum TabOptions {
  TOP_ARTISTS = "artist",
  TOP_SONGS = "track",
  TOP_ALBUMS = "album"
}

const SpotifyChartEntries = ({
  paginatedSpotifyChartEntries,
  fetchSpotifyChartEntries,
  isAdmin
}: {
  paginatedSpotifyChartEntries: PaginatedData<SpotifyChartEntry>
  fetchSpotifyChartEntries: (
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined
  ) => Promise<void>
  isAdmin: boolean
}) => {
  const [loading, setLoading] = useState(true)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [timeoutId, setTimeoutId] = useState(undefined)
  const [inputTimeoutId, setInputTimeoutId] = useState(undefined)
  const [additionalFilters, setAdditionalFilters] = useState<{ [key: string]: string | undefined }>({})
  const [searchValue, setSearchValue] = useState<string>("")

  const baseSearchOptions: KSelectOption[] = [
    { label: lang.spotify_chart_entries.chart_name, value: 0, value2: "spotify_chart_name", type: "all" },
    { label: lang.spotify_chart_entries.chart_alias, value: 1, value2: "spotify_chart_alias", type: "all" },
    { label: lang.spotify_chart_entries.track_title, value: 2, value2: "track_name", type: "track" },
    { label: lang.spotify_chart_entries.isrc, value: 3, value2: "isrc", type: "track" },
    { label: lang.spotify_chart_entries.release_title, value: 4, value2: "release_name", type: "track,album" },
    { label: lang.spotify_chart_entries.artist, value: 5, value2: "artist_name", type: "all" },
    { label: lang.spotify_chart_entries.country_code, value: 6, value2: "country_code", type: "all" },
    { label: lang.spotify_chart_entries.upc, value: 7, value2: "upc", type: "album" }
  ]

  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>()
  const navigate = useNavigate()
  const [openSidebar, setOpenSidebar] = useState<boolean>(true)
  const [selectedChartType, setSelectedChartType] = useState(TabOptions.TOP_SONGS)
  const [searchOptions, setSearchOptions] = useState<KSelectOption[]>([])
  const [selectedSearchOption, setSelectedSearchOption] = useState<KSelectOption>(baseSearchOptions[0])

  const [dateValues, setDateValues] = useState<DateRangeType>([null, null])
  const [maxDate, setMaxDate] = useState<Date>()
  const [minDate, setMinDate] = useState<Date>()
  const [noData, setNoData] = useState<boolean>(false)

  const [language, setLanguage] = useState<string>(() => getSelectedLanguage())

  const tableColumns: TableColumn[] = spotifyChartEntriesColumns(selectedChartType)
  const [columns, setColumns] = useState<TableColumn[]>(tableColumns)

  useEffect(() => {
    // loading=true here on purpose; it's flipped back to false later
    // in onSortOrFilterChange once the debounced fetch resolves
    setLoading(true)
    const tableColumns: TableColumn[] = spotifyChartEntriesColumns(selectedChartType)
    setColumns(tableColumns)

    const filteredOptions = baseSearchOptions.filter(
      (option) => option?.type && (option?.type === "all" || option?.type?.includes(selectedChartType))
    )
    setSearchOptions(filteredOptions)
    setSelectedSearchOption(baseSearchOptions[0])
    setSearchValue("")
  }, [selectedChartType])

  const endDateString = useMemo(() => {
    const dates = dateValues as Date[]

    if (!dates[0]) return undefined
    const dashedDate = toDashedYYYYMMDD(dates[1])

    return dashedDate
  }, [dateValues])

  const fetchLatestDate = async () => {
    let userId = ""
    if (isAdmin && selectedUser) {
      userId = `${selectedUser.value}`
    } else if (isAdmin && !selectedUser) {
      userId = "0"
    }

    const isAdminParam = isAdmin ? "&is_admin_page=1" : ""
    const response = await spotifyChartEntries(
      `/available_date_range/?${userId ? `user_id=${userId}` : ""}${isAdminParam}`,
      "get"
    )
    const earliestDate: string = response?.data.start_date
    const latestDate: string = response?.data.end_date
    if (!latestDate && !earliestDate) {
      setNoData(true)
    }
    const defaultDates = getDefaultDatesForSpotifyChart(latestDate)
    setDateValues([defaultDates.defaultStartDate, defaultDates.defaultEndDate])
    setMaxDate(defaultDates.defaultEndDate)
    if (earliestDate) {
      const minDate = parseDate(earliestDate)
      setMinDate(minDate)
    } else {
      setMinDate(defaultDates.defaultStartDate)
    }
    setMaxDate(defaultDates.defaultEndDate)
  }

  useEffect(() => {
    fetchLatestDate()
  }, [])

  useEffect(() => {
    if (!endDateString) return
    let userId = ""
    if (isAdmin && selectedUser) {
      userId = `${selectedUser.value}`
    } else if (isAdmin && !selectedUser) {
      userId = "0"
    }

    clearTimeout(inputTimeoutId)
    setInputTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        if (selectedSearchOption?.value2) {
          setAdditionalFilters({
            spotify_chart_name: selectedSearchOption?.value2 === "spotify_chart_name" ? searchValue : "",
            spotify_chart_alias: selectedSearchOption?.value2 === "spotify_chart_alias" ? searchValue : "",
            track_name: selectedSearchOption?.value2 === "track_name" ? searchValue : "",
            isrc: selectedSearchOption?.value2 === "isrc" ? searchValue : "",
            upc: selectedSearchOption?.value2 === "upc" ? searchValue : "",
            release_name: selectedSearchOption?.value2 === "release_name" ? searchValue : "",
            artist_name: selectedSearchOption?.value2 === "artist_name" ? searchValue : "",
            country_code: selectedSearchOption?.value2 === "country_code" ? searchValue : "",
            user_id: userId,
            chart_type: selectedChartType,
            chart_date: endDateString, // YYYY-MM-DD
            is_admin_page: isAdmin ? "1" : ""
          })
          setPageNumber(1)
        }
      }, 500)
    )
  }, [selectedSearchOption, searchValue, selectedUser, selectedChartType, endDateString])

  const limit = 10

  const tableTitle = (
    <div className="flex flex-row gap-2 items-center !cursor-default self-start">
      <KTooltip
        backgroundColor="#000"
        padding="0px 4px"
        height="20px"
        arrowColor="#000"
        position="bottom"
        marginLeft={!openSidebar && language === "tr" ? "30px" : "0px"}
        content={
          <div className="h-5 w-max flex items-center">
            <KSpan
              text={openSidebar ? lang.spotify_chart_entries.hide_sidebar : lang.spotify_chart_entries.show_sidebar}
              fontSize={12}
              lineHeight="16px"
              color="#FFF"
            />
          </div>
        }
      >
        <div className="flex items-center">
          <KButton
            icon="/left-sidebar.svg"
            width="32px"
            height="32px"
            background="transparent"
            shadowDisabled={true}
            padding="4px"
            onClick={() => {
              if (setOpenSidebar) {
                setOpenSidebar(!openSidebar)
              }
              setLoading(true)
              setTimeout(() => {
                setLoading(false)
              }, 250)
            }}
          />
        </div>
      </KTooltip>
      <div>
        <KTitleSpan
          text={lang.spotify_chart_entries.title}
          color="#111"
          fontSize={isAdmin ? 20 : 28}
          lineHeight="28px"
        />
      </div>
    </div>
  )

  const onClickPage = async (
    pageNumber: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => {
    setLoading(true)
    await fetchSpotifyChartEntries(limit, (pageNumber - 1) * limit, filters)
    setLoading(false)
    setPageNumber(pageNumber)
  }

  const onSortOrFilterChange = async (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => {
    // runs in first render (mount) without applying any filter or sort param
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setTimeoutId(
      //@ts-ignore
      setTimeout(
        async () => {
          setLoading(true)
          await fetchSpotifyChartEntries(limit, 0, filters)
          setLoading(false)
          setPageNumber(1)
        },
        debounce ? 500 : 0
      )
    )
  }

  const topRightElements = (
    <div className="flex items-center gap-4">
      <div className="flex flex-row items-center gap-3">
        {(dateValues as Date[])[1] && (
          <KSpan
            text={`${lang.spotify_chart_entries.week_of.replace(
              "<date_param>",
              formatDate(toYYYYMMDD((dateValues as Date[])[1]))
            )}`}
            color="#111"
            fontWeight={500}
          />
        )}
        <KSelectRangeDate
          value={dateValues}
          onChange={(dateRange) => {
            setDateValues(dateRange)
          }}
          maximumDate={maxDate}
          minimumDate={minDate}
          height="36px"
          width="36px"
          backgroundColor="#F7F7F7"
          hoverBackgroundColor="#F3F3F3"
          boxShadow="none"
          weeklyMode={true}
          weeklyModeStartDay={5} // Friday
        />
      </div>
      <img src="/vector.svg" alt="vector-icon" />
      <div
        className="flex items-center py-[13px] pl-3.5 pr-3 gap-2 rounded-[10px] w-[300px]"
        style={{
          background: searchValue ? "#FFF" : "#F7F7F7",
          border: "1px solid #F3F3F3",
          height: "48px",
          transition: "0.3s"
        }}
      >
        <KInput
          dataTestId="spotify-chart-entry-search"
          value={searchValue}
          padding="0px"
          background="transparent"
          onChange={(text: string) => {
            setSearchValue(text)
          }}
          leftIcon="/catalog_icons/search-grey.svg"
          gap="8px"
          borderRadius={0}
          shadowDisabled
          placeholder={"Search..."}
        />
        <KDropdown
          dataTestId="playlist-search-option"
          rightIcon="/admin_icons/caret-down.svg"
          options={searchOptions}
          selected={selectedSearchOption}
          onSelect={(selected) => {
            setSelectedSearchOption(selected as KSelectOption)
          }}
          padding="0px"
          background="transparent"
          activeBackground="transparent"
          shadowDisabled={true}
          enableIndicator={true}
          menuWidth={140}
          width={180}
          menuLeftMargin={-40}
          textColor="#F3F3F3"
        />
      </div>
    </div>
  )

  const topFilters = (
    <SearchUserComponent className="px-0 mb-6" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
  )

  return (
    <div className="catalog flex-grow justify-center">
      <div className="w-[100%] flex ">
        <div
          className={`${
            openSidebar ? "min-w-[230px]" : "min-w-0 !w-[0px]"
          } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden shrink-0`}
          style={{
            transitionDuration: "0.5s"
          }}
        >
          <div
            className="pr-3 py-4 whitespace-nowrap flex gap-2"
            style={{
              paddingLeft: isAdmin ? 24 : 16
            }}
          >
            {!isAdmin && (
              <div
                style={{ textAlign: "left", display: "inline-block", cursor: "pointer" }}
                onClick={() => navigate(-1)}
              >
                <KLogo hoverEnabled={true} width={36} height={36} logoColor={"white"} borderRadius={1} />
              </div>
            )}
            <KTitleSpan text={lang.spotify_chart_entries.title} lineHeight="28px" fontSize={20} color="#111" />
          </div>

          <div>
            <div className="py-1.5 px-4">
              <div
                data-testid="left-bar-apple"
                className={`rounded-[10px] p-2 flex flex-row gap-1 mt-1 items-center ${
                  selectedChartType === TabOptions.TOP_SONGS ? "bg-[#FFF]" : "transparent"
                } cursor-pointer whitespace-nowrap`}
                onClick={() => {
                  if (selectedChartType === TabOptions.TOP_SONGS) return
                  setSelectedChartType(TabOptions.TOP_SONGS)
                }}
              >
                <img src={TrackLogo} className="h-6 w-auto" />
                <KSpan
                  text={lang.spotify_chart_entries.top_songs}
                  fontSize={14}
                  lineHeight="20px"
                  fontWeight={500}
                  color={"#111"}
                />
              </div>
              <div
                data-testid="left-bar-apple"
                className={`rounded-[10px] p-2 flex flex-row gap-1 mt-1 items-center ${
                  selectedChartType === TabOptions.TOP_ALBUMS ? "bg-[#FFF]" : "transparent"
                } cursor-pointer whitespace-nowrap`}
                onClick={() => {
                  if (selectedChartType === TabOptions.TOP_ALBUMS) return
                  setSelectedChartType(TabOptions.TOP_ALBUMS)
                }}
              >
                <img src={AlbumLogo} className="h-6 w-auto" />
                <KSpan
                  text={lang.spotify_chart_entries.top_albums}
                  fontSize={14}
                  lineHeight="20px"
                  fontWeight={500}
                  color={"#111"}
                />
              </div>
              <div
                data-testid="left-bar-apple"
                className={`rounded-[10px] p-2 flex flex-row gap-1 mt-1 items-center ${
                  selectedChartType === TabOptions.TOP_ARTISTS ? "bg-[#FFF]" : "transparent"
                } cursor-pointer whitespace-nowrap`}
                onClick={() => {
                  if (selectedChartType === TabOptions.TOP_ARTISTS) return
                  setSelectedChartType(TabOptions.TOP_ARTISTS)
                }}
              >
                <img src={ArtistLogo} className="h-6 w-auto" />
                <KSpan
                  text={lang.spotify_chart_entries.top_artists}
                  fontSize={14}
                  lineHeight="20px"
                  fontWeight={500}
                  color={"#111"}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="grow min-h-0">
          <TableNew
            // className="!overflow-hidden"
            title={tableTitle}
            pagination
            minRowCount={limit}
            limit={limit}
            columns={columns}
            data={paginatedSpotifyChartEntries.results}
            count={paginatedSpotifyChartEntries.count}
            loading={loading}
            topFilters={isAdmin ? topFilters : undefined}
            onClickPage={onClickPage}
            topRightElement={topRightElements}
            onSortOrFilterChange={onSortOrFilterChange}
            pageNumber={pageNumber}
            overrideTableOverflow="auto"
            additionalFilters={additionalFilters}
            notFoundTitle={lang.not_found.spotify_chart_entries.title}
            notFoundDesc={
              noData ? lang.not_found.spotify_chart_entries.desc_2 : lang.not_found.spotify_chart_entries.desc
            }
          />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    paginatedSpotifyChartEntries: state.insights.paginatedSpotifyChartEntries
  }
}

export default connect(mapStateToProps, { fetchSpotifyChartEntries })(SpotifyChartEntries)
